import React, { useState, useEffect } from 'react';

// Define 8 initial target positions.
const initialTargets = [
  { id: 1, left: "10%", top: "20%" },
  { id: 2, left: "30%", top: "15%" },
  { id: 3, left: "50%", top: "30%" },
  { id: 4, left: "70%", top: "20%" },
  { id: 5, left: "20%", top: "60%" },
  { id: 6, left: "40%", top: "70%" },
  { id: 7, left: "60%", top: "65%" },
  { id: 8, left: "80%", top: "55%" },
];

// Function to initialize game targets with exactly 4 targets having a hidden dollar.
const initializeGameTargets = () => {
  // Clone the initial targets and add hit state.
  let targetsCopy = initialTargets.map((t) => ({ ...t, isHit: false }));
  // Randomly choose 4 distinct indices to be "dollar" targets.
  const indicesWithDollar = [];
  while (indicesWithDollar.length < 4) {
    const idx = Math.floor(Math.random() * targetsCopy.length);
    if (!indicesWithDollar.includes(idx)) {
      indicesWithDollar.push(idx);
    }
  }
  // Assign hasDollar true for those indices; others get false.
  targetsCopy = targetsCopy.map((t, idx) => ({ ...t, hasDollar: indicesWithDollar.includes(idx) }));
  return targetsCopy;
};

const ShootingGame = ({ onBack }) => {
  const [gameStatus, setGameStatus] = useState("idle"); // "idle", "running", "won", "lost"
  const [bulletCount, setBulletCount] = useState(4);
  const [dollarHits, setDollarHits] = useState(0);
  const [targets, setTargets] = useState([]);

  // Start/restart the game.
  const startGame = () => {
    setGameStatus("running");
    setBulletCount(4);
    setDollarHits(0);
    setTargets(initializeGameTargets());
  };

  const restartGame = () => {
    startGame();
  };

  // Update positions of moving targets every 2 seconds (for targets not hit).
  useEffect(() => {
    if (gameStatus !== "running") return;
    const moveInterval = setInterval(() => {
      setTargets((prevTargets) =>
        prevTargets.map((target) => {
          if (target.isHit) return target; // Do not move already hit targets.
          // Generate new random positions (avoid extremes: between 10% and 85%).
          const newLeft = Math.floor(Math.random() * 76 + 10) + "%";
          const newTop = Math.floor(Math.random() * 76 + 10) + "%";
          return { ...target, left: newLeft, top: newTop };
        })
      );
    }, 2000);
    return () => clearInterval(moveInterval);
  }, [gameStatus]);

  // Handle container click (a miss).
  const handleContainerClick = () => {
    if (gameStatus !== "running") return;
    const newBulletCount = bulletCount - 1;
    setBulletCount(newBulletCount);
    if (newBulletCount <= 0 && dollarHits < 3) {
      setGameStatus("lost");
    }
  };

  // Handle clicking on a bullseye.
  const handleBullseyeClick = (e, targetId) => {
    e.stopPropagation(); // Prevent container click.
    if (gameStatus !== "running") return;

    const target = targets.find((t) => t.id === targetId);
    if (target.isHit) return; // Already hit.

    // Mark target as hit.
    const updatedTargets = targets.map((t) =>
      t.id === targetId ? { ...t, isHit: true } : t
    );
    setTargets(updatedTargets);

    // Deduct bullet.
    const newBulletCount = bulletCount - 1;
    setBulletCount(newBulletCount);

    // If this target is a dollar target, increment dollarHits.
    let newDollarHits = dollarHits;
    if (target.hasDollar) {
      newDollarHits = dollarHits + 1;
      setDollarHits(newDollarHits);
    }
    // Check win condition: 3 or more dollar hits wins.
    if (newDollarHits >= 3) {
      setGameStatus("won");
    } else if (newBulletCount <= 0 && newDollarHits < 3) {
      setGameStatus("lost");
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6 bg-white shadow rounded-lg my-6">
      <h1 className="text-2xl font-bold text-center mb-4">Hit the Dollar</h1>
      <button onClick={onBack} className="mb-4 text-blue-500 hover:underline">
        &larr; Back to Menu
      </button>

      {gameStatus === "idle" && (
        <div className="text-center">
          <button
            onClick={startGame}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Start Game
          </button>
        </div>
      )}

      {gameStatus === "running" && (
        <>
          <div className="flex justify-center space-x-6 mb-4">
            <p className="font-semibold">Bullets: {bulletCount}</p>
            <p className="font-semibold">Dollar Hits Required to Win: {dollarHits} / 3</p>
          </div>
          {/* Shooting range */}
          <div
            className="relative w-full h-96 bg-green-100 border border-green-300 rounded cursor-crosshair"
            onClick={handleContainerClick}
          >
            {targets.map((target) => (
              <div
                key={target.id}
                className="absolute"
                style={{ left: target.left, top: target.top }}
              >
                {/* Outer circle representing the target */}
                <div className="relative w-20 h-20 rounded-full border-4 border-red-500 flex items-center justify-center">
                  {/* Inner circle representing the bullseye */}
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold cursor-pointer"
                    style={{ backgroundColor: "red", opacity: target.isHit ? "1" : "0.8" }}
                    onClick={(e) => handleBullseyeClick(e, target.id)}
                  >
                    {target.isHit &&
                      (target.hasDollar ? (
                        <span className="text-2xl">💲</span>
                      ) : (
                        <span className="text-2xl">😢</span>
                      ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {gameStatus === "won" && (
        <div className="text-center">
          <h2 className="text-xl font-bold text-green-600 mb-2">You Won!</h2>
          <p className="mb-4">You successfully hit 3 dollar targets!</p>
          <button
            onClick={restartGame}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Restart Game
          </button>
        </div>
      )}

      {gameStatus === "lost" && (
        <div className="text-center">
          <h2 className="text-xl font-bold text-red-600 mb-2">Game Over!</h2>
          <p className="mb-4">You ran out of bullets without hitting 3 dollar targets.</p>
          <button
            onClick={restartGame}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Restart Game
          </button>
        </div>
      )}
    </div>
  );
};

export default ShootingGame;
